import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, Check, Download } from '../../../icons';
import {
  getEmployee, updateEmployeeHr, EMPLOYMENT_STATUS_LABEL, type EmployeeDetailDto,
} from '../../../api/hr-employee';
import { employmentTypeOptions } from '../../../lib/employment-type-i18n';
import { getSharedLang, subscribeSharedLang } from '../../../lib/base-i18n';
import { fetchPersonCard, putPersonAvatar, deletePersonAvatar, type PersonCardDto } from '../../../api/hr-people';
import { listWorksites, type WorksiteDto } from '../../../api/hr-worksite';
import { JobGradeSelect } from '../../../components/JobGradeSelect';
import { useCompanyCodes } from '../../../lib/company-codes';
import { avatarImageUrl } from '../../../lib/avatar-src';
import { EMPTY_EXTRA, NOT_READY_TEXT, type PersonExtraFields } from './employee-hr-types';
import {
  AVATAR_ERRORS, AVATAR_MAX_BYTES, AVATAR_MIME, BIRTHDAY_RE, EMPLOYEES_PATH, ERRORS, STAFF_PATH, STATUS_TONE, employeeCardPath, errorCode, initialOf,
  PROBATION_END_LABEL, SUPERVISOR_SOURCE_LABEL, UPCOMING_HIRE_LABEL, isUpcomingHire, probationDefaultNote, resignBadge,
} from './employees-shared';
import { Field } from './EmployeeField';
import './employees-hr.css';

/**
 * 직원 상세 (2026-09-13 · H · `/erp/hr/employees/:userId`).
 *
 * 시안 axi-employees-hr-detail: 머리(사진 · 이름 · 배지 · 입력률) → 탭 6(인사정보 · 경력 · 학력 · 자격·어학 · 가족 · 발령 이력)
 * → 2열 폼 → 하단 저장 막대.
 *
 * ★ 편집 규칙은 종전 명부(`AttendancePage/EmployeesPage.tsx`)와 **같다** — 고친 칸만 PATCH, 이름 비움 · 생일 형식은 화면이 먼저
 *   막는다, 사람 카드를 못 읽으면 영문 이름 · 생일은 잠근다, 무엇이 열리는지는 서버 `editable` 이 정한다,
 *   사진은 전사를 보는 사람만. 저장 뒤 다시 읽어도 「저장했습니다」 가 남는다.
 * ★ 서버에 아직 없는 칸 · 탭은 자리만 그린다(「데이터 준비 중」) — 타입은 `employee-hr-types.ts`.
 */
type IdentityKey = 'name' | 'nameEn' | 'birthday';
type TabKey = 'info' | 'career' | 'edu' | 'cert' | 'family' | 'moves';

const TABS: ReadonlyArray<[TabKey, string]> = [
  ['info', '인사정보'], ['career', '경력'], ['edu', '학력'], ['cert', '자격 · 어학'], ['family', '가족'], ['moves', '발령 이력'],
];

export function EmployeeDetailPage({ extra = EMPTY_EXTRA }: { extra?: PersonExtraFields }): JSX.Element {
  const { userId = '' } = useParams();
  const codes = useCompanyCodes('hr');
  // 고용형태 칩 — 수습 · 인턴 포함(서버 #1222), 이름은 언어를 따르고 회사 코드가 그 위에 덮는다.
  const lang = useSyncExternalStore(subscribeSharedLang, getSharedLang, getSharedLang);
  const employmentTypes = codes.apply('employment_type', employmentTypeOptions(lang));

  const [detail, setDetail] = useState<EmployeeDetailDto | null>(null);
  const [card, setCard] = useState<PersonCardDto | null>(null);
  const [hrDraft, setHrDraft] = useState<Record<string, string>>({});
  const [idDraft, setIdDraft] = useState<Partial<Record<IdentityKey, string>>>({});
  const [tab, setTab] = useState<TabKey>('info');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [worksites, setWorksites] = useState<WorksiteDto[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cardFor = useRef<string | null>(null);

  const loadCard = useCallback((id: string) => {
    cardFor.current = id;
    fetchPersonCard(id)
      .then((c) => { if (cardFor.current === id) setCard(c); })
      .catch(() => { if (cardFor.current === id) setCard(null); });
  }, []);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const d = await getEmployee(id);
      setDetail(d);
      setHrDraft({});
      setIdDraft({});
      loadCard(id);
    } catch (err) {
      setError(ERRORS[errorCode(err)] ?? '직원 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [loadCard]);

  useEffect(() => { setCard(null); setDone(null); setError(null); void load(userId); }, [userId, load]);
  // 근무지 고르기 목록(계약 §18.3 — 인사 담당에게 열렸다). 못 읽으면 근무지는 이름만 보인다.
  useEffect(() => {
    listWorksites().then((d) => setWorksites(d.worksites)).catch(() => setWorksites(null));
  }, []);

  const hrValue = (key: keyof EmployeeDetailDto['hr']): string => {
    if (key in hrDraft) return hrDraft[key] ?? '';
    const v = detail?.hr[key];
    return v === null || v === undefined ? '' : String(v);
  };
  const idValue = (key: IdentityKey): string => {
    if (key in idDraft) return idDraft[key] ?? '';
    if (key === 'name') return detail?.self.name ?? '';
    const v = card?.person[key];
    return v === null || v === undefined ? '' : String(v);
  };
  const dirtyCount = Object.keys(hrDraft).length + Object.keys(idDraft).length;
  const dirty = dirtyCount > 0;

  const onSave = async () => {
    if (!detail || !dirty) return;
    if ('name' in idDraft && !(idDraft.name ?? '').trim()) { setDone(null); setError('이름은 비울 수 없습니다.'); return; }
    if ('birthday' in idDraft && (idDraft.birthday ?? '').trim() && !BIRTHDAY_RE.test((idDraft.birthday ?? '').trim())) {
      setDone(null); setError('생일은 MM-DD 형식으로 적어 주세요 (예: 03-14).'); return;
    }
    setBusy(true); setError(null); setDone(null);
    try {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(hrDraft)) body[k] = k === 'jobLevel' ? (v ? Number(v) : null) : (v || null);
      if ('name' in idDraft) body.name = (idDraft.name ?? '').trim();
      if ('nameEn' in idDraft) body.nameEn = (idDraft.nameEn ?? '').trim() || null;
      if ('birthday' in idDraft) body.birthday = (idDraft.birthday ?? '').trim() || null;
      const res = await updateEmployeeHr(userId, body);
      const msg = res.annualDays === null ? '저장했습니다.' : `저장했습니다 — 올해 연차 ${res.annualDays}일로 다시 계산됐습니다.`;
      await load(userId);
      setDone(msg);
    } catch (err) {
      setError(ERRORS[errorCode(err)] ?? '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  // 사진 — PersonHead 와 같은 규격 · 같은 권한 판정
  const avatarUrl = card?.person.avatarUrl ?? detail?.self.avatarUrl ?? null;
  const avatarSrc = avatarImageUrl(userId, avatarUrl);
  const canEditAvatar = card?.scope === 'ALL' || (card?.scope === 'SELF' && !!detail?.editable.HR);
  const runAvatar = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null); setDone(null);
    try { await fn(); loadCard(userId); } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status ?? 0;
      setError(AVATAR_ERRORS[status] ?? '사진을 바꾸지 못했습니다.');
    } finally { setBusy(false); }
  };
  const pickAvatar = (file: File | undefined) => {
    if (!file) return;
    if (!AVATAR_MIME.includes(file.type)) { setError('PNG · JPEG · WEBP 만 올릴 수 있습니다.'); return; }
    if (file.size > AVATAR_MAX_BYTES) { setError('이미지는 2MB 이하만 올릴 수 있습니다.'); return; }
    void runAvatar(() => putPersonAvatar(userId, file));
  };

  // 입력률 — 지금 서버에 있는 칸 기준(후속 서버 요청 ③ 이 오면 서버 값으로 바꾼다)
  const fill = useMemo(() => {
    if (!detail) return 0;
    const vals = [detail.self.name, card?.person.nameEn, card?.person.birthday, detail.hr.hireDate, detail.hr.position,
      detail.hr.employmentType, detail.self.phone, detail.account.employeeNumber, detail.hr.teamId];
    return Math.round((vals.filter((v) => v !== null && v !== undefined && v !== '').length / vals.length) * 100);
  }, [detail, card]);

  if (loading && !detail) {
    return <div className="hre"><p className="hre-path">불러오는 중…</p></div>;
  }
  if (!detail) {
    return (
      <div className="hre">
        <nav className="hre-crumb" aria-label="위치"><span>인사</span><i>/</i><Link to={EMPLOYEES_PATH}>직원 관리</Link></nav>
        <p className="hre-alert" role="alert"><AlertCircle aria-hidden />{error ?? '직원 정보를 불러오지 못했습니다.'}</p>
      </div>
    );
  }

  const hr = detail.editable.HR;
  // 입력률 — 서버 집계(§18.6 · 아홉 칸 · 공백은 미입력)가 있으면 그 값, 없으면(옛 서버) 화면 계산.
  const rate = detail.hr.inputRate ?? fill;
  // 서버 칸(계약 §18.3) — `undefined` 는 옛 서버(칸 자체가 없음)라 종전 자리표시를 그린다. `null` 은 «값이 없음».
  const callTitle = detail.hr.title !== undefined ? detail.hr.title : detail.hr.position;
  const supervisorText = detail.hr.supervisor
    ? `${detail.hr.supervisor.name} · ${SUPERVISOR_SOURCE_LABEL[detail.hr.supervisor.source] ?? detail.hr.supervisor.source}`
    : '없음';
  const jobRolesText = (detail.hr.jobRoles ?? [])
    .map((j) => `${j.name}${j.orgName ? ` · ${j.orgName}` : ''}${j.isConcurrent ? ' (겸직)' : ''}`)
    .join(', ') || '없음';
  const typeLabel = employmentTypes.find((t) => t.key === detail.hr.employmentType)?.label;
  /** 서버에 아직 없는 칸 — 값이 오면(⑥) 읽기 칸으로 선다. */
  const extraField = (label: string, value: string | null, placeholder: string, wide = false) => (
    <Field label={label} wide={wide}>
      {(a) => (value === null
        ? <input {...a} className="hre-in" type="text" value="" placeholder={`${placeholder} — ${NOT_READY_TEXT}`} disabled />
        : <input {...a} className="hre-in" type="text" value={value} readOnly />)}
    </Field>
  );

  return (
    <div className="hre">
      <header className="hre-top">
        <div>
          <nav className="hre-crumb" aria-label="위치">
            <span>인사</span><i>/</i><Link to={EMPLOYEES_PATH}>직원 관리</Link><i>/</i><span>{detail.self.name}</span>
          </nav>
          <h1 className="hre-title">직원 상세</h1>
        </div>
      </header>

      {error && <p className="hre-alert" role="alert"><AlertCircle aria-hidden />{error}</p>}
      {done && !error && <p className="hre-ok" role="status"><Check aria-hidden />{done}</p>}

      <section className="hre-card hre-detail" aria-label="직원 상세">
        <div className="hre-heads">
          <span className="hre-avatar hre-avatar--lg">
            {avatarSrc ? <img src={avatarSrc} alt={`${detail.self.name} 사진`} /> : <span aria-hidden>{initialOf(detail.self.name)}</span>}
          </span>
          <div className="hre-eh-main">
            <div className="hre-eh-name">
              <h2>{detail.self.name}</h2>
              {isUpcomingHire(detail.hr) ? (
                <span className="hre-badge hre-badge--pur">{UPCOMING_HIRE_LABEL}</span>
              ) : (
                <span className={`hre-badge hre-badge--${STATUS_TONE[detail.hr.employmentStatus] ?? 'slate'}`}>
                  {EMPLOYMENT_STATUS_LABEL[detail.hr.employmentStatus] ?? detail.hr.employmentStatus}
                </span>
              )}
              {/* 퇴사 예정 · 처리 대기(§18.4) — 재직 배지 옆에 더한다 */}
              {(() => { const rb = resignBadge(detail.hr); return rb && <span className={`hre-badge hre-badge--${rb.tone}`} title={rb.title}>{rb.label}</span>; })()}
              {typeLabel && <span className="hre-badge hre-badge--slate">{typeLabel}</span>}
              <span className="hre-badge hre-badge--slate">{detail.account.role}</span>
            </div>
            <p>{detail.hr.divisionName ? `${detail.hr.divisionName} · ` : ''}{detail.hr.teamName ?? '팀 미배정'} 소속{callTitle ? ` ${callTitle}` : ''}
              {detail.hr.worksiteId !== undefined && <> · 근무지 {detail.hr.worksiteName ?? '본사 소속'}</>}</p>
            <p className="hre-meta num">
              {detail.account.employeeNumber ?? '사번 없음'} · {detail.hr.hireDate ? `${detail.hr.hireDate} 입사` : '입사일 없음'}
              {' · '}근속 {detail.hr.tenureLabel ?? '—'} · 올해 연차 {detail.hr.annualDays === null ? '미설정' : `${detail.hr.annualDays}일`}
              {detail.hr.probationEndDate && (
                <>{' · '}{PROBATION_END_LABEL} {detail.hr.probationEndDate}{detail.hr.probationMonthsSource === 'DEFAULT' && ` (${probationDefaultNote()})`}</>
              )}
            </p>
          </div>
          <div className="hre-eh-fill" title={detail.hr.inputRate !== undefined ? '필수 인사정보 아홉 칸 입력률 — 서버 집계' : '지금 서버에 있는 칸 기준 입력률'}>
            <span>입력률</span><b className="num">{rate}%</b>
            <span className="hre-bar"><i className={rate < 100 ? 'warn' : undefined} style={{ width: `${rate}%` }} /></span>
          </div>
        </div>

        <div className="hre-tabbar">
          <div className="hre-tabs" role="tablist" aria-label="직원 정보">
            {TABS.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>{label}</button>
            ))}
          </div>
          <div className="hre-end">
            {canEditAvatar && (
              <>
                <button type="button" className="hre-btn hre-btn--line hre-btn--sm" disabled={busy}
                  aria-label={avatarSrc ? `${detail.self.name} 사진 바꾸기` : `${detail.self.name} 사진 등록`}
                  onClick={() => fileRef.current?.click()}>{avatarSrc ? '사진 바꾸기' : '사진 등록'}</button>
                {avatarSrc && (
                  <button type="button" className="hre-btn hre-btn--line hre-btn--sm" disabled={busy}
                    onClick={() => { void runAvatar(() => deletePersonAvatar(userId)); }}>사진 지우기</button>
                )}
                <input ref={fileRef} className="hre-file" type="file" accept={AVATAR_MIME.join(',')} tabIndex={-1} aria-hidden
                  onChange={(e) => { pickAvatar(e.target.files?.[0]); e.target.value = ''; }} />
              </>
            )}
            {/* 인사카드 — 서버 카드 JSON 을 인쇄용 화면으로 그린다(계약 §18.7 · 과제 J). */}
            <Link className="hre-btn hre-btn--line hre-btn--sm" to={employeeCardPath(userId)} title="인사카드 — 인쇄 · PDF 저장">
              <Download aria-hidden />인사카드
            </Link>
          </div>
        </div>

        <div className="hre-panes" role="tabpanel" aria-label={TABS.find(([k]) => k === tab)?.[1]}>
          {tab === 'info' && (
            <>
              <section className="hre-fsec"><h3>기본 정보</h3><div className="hre-fgrid">
                <Field label="성명" required hint="본인은 못 고치고 인사가 여기서 고칩니다">
                  {(a) => <input {...a} className="hre-in" type="text" value={idValue('name')} maxLength={50} disabled={!hr}
                    onChange={(e) => setIdDraft({ ...idDraft, name: e.target.value })} />}
                </Field>
                <Field label="영문 성명">
                  {(a) => <input {...a} className="hre-in" type="text" value={idValue('nameEn')} maxLength={100} placeholder="여권과 같은 표기"
                    disabled={!hr || !card} onChange={(e) => setIdDraft({ ...idDraft, nameEn: e.target.value })} />}
                </Field>
                <Field label="생일" hint="연도 없이 MM-DD">
                  {(a) => <input {...a} className="hre-in num" type="text" value={idValue('birthday')} maxLength={5} placeholder="MM-DD"
                    disabled={!hr || !card} onChange={(e) => setIdDraft({ ...idDraft, birthday: e.target.value })} />}
                </Field>
                <div className="hre-f"><span className="hre-fl" id="hre-gender-label">성별</span>
                  <span className="hre-chips" role="radiogroup" aria-labelledby="hre-gender-label">
                    {(['F', 'M'] as const).map((g) => (
                      <label key={g} className="hre-choice"><input type="radio" name="hre-gender" checked={extra.gender === g} disabled readOnly /><span>{g === 'F' ? '여' : '남'}</span></label>
                    ))}
                  </span>
                  {extra.gender === null && <small>{NOT_READY_TEXT}</small>}</div>
                <Field label="휴대폰" hint="본인이 내 프로필에서 고칩니다">
                  {(a) => <input {...a} className="hre-in num" type="text" value={detail.self.phone ?? ''} placeholder="—" readOnly />}
                </Field>
                <Field label="내선" hint="본인이 내 프로필에서 고칩니다">
                  {(a) => <input {...a} className="hre-in num" type="text" value={detail.self.extension ?? ''} placeholder="—" readOnly />}
                </Field>
                {extraField('개인 이메일', extra.personalEmail, '개인 연락용 이메일')}
                {extraField('비상 연락처', extra.emergencyContact, '이름 · 관계 · 번호')}
                {extraField('주소', extra.address, '도로명 주소', true)}
              </div></section>

              <section className="hre-fsec"><h3>근무 정보</h3><div className="hre-fgrid">
                <Field label="사번" hint="관리 콘솔의 사용자 관리에서 발급 · 로그인 식별자">
                  {(a) => <input {...a} className="hre-in num" type="text" value={detail.account.employeeNumber ?? ''} placeholder="—" readOnly />}
                </Field>
                <Field label="입사일" hint="올해 연차를 계산하는 기준">
                  {(a) => <input {...a} className="hre-in num" type="date" value={hrValue('hireDate')} disabled={!hr}
                    onChange={(e) => setHrDraft({ ...hrDraft, hireDate: e.target.value })} />}
                </Field>
                <Field label="소속" hint="팀 배정은 조직 관리에서">
                  {(a) => <input {...a} className="hre-in" type="text" value={`${detail.hr.divisionName ? `${detail.hr.divisionName} › ` : ''}${detail.hr.teamName ?? '팀 미배정'}`} readOnly />}
                </Field>
                {detail.hr.supervisor !== undefined ? (
                  <Field label="상위자" hint="팀장 → 본부장 → 대표 순으로 찾은 보여 주기용 값 · 결재선과는 따로입니다">
                    {(a) => <input {...a} className="hre-in" type="text" value={supervisorText} readOnly />}
                  </Field>
                ) : extraField('직속 상관', extra.managerName, '직속 상관')}
                <Field label="직위" hint="회사 직급표에서 고릅니다">
                  {(a) => <JobGradeSelect id={a.id} className="hre-in" value={hrValue('position')} disabled={!hr} manage
                    onChange={(v) => setHrDraft({ ...hrDraft, position: v })} />}
                </Field>
                {detail.hr.jobRoles !== undefined ? (
                  <>
                    <Field label="직책" hint="지금 맡은 보임 · 바꾸는 길은 발령입니다">
                      {(a) => <input {...a} className="hre-in" type="text" value={jobRolesText} readOnly />}
                    </Field>
                    <Field label="호칭" hint="대표 직책의 호칭 · 직책이 없으면 직위">
                      {(a) => <input {...a} className="hre-in" type="text" value={callTitle ?? ''} placeholder="—" readOnly />}
                    </Field>
                  </>
                ) : extraField('직책', extra.duty, '직책')}
                <div className="hre-f hre-f--wide"><span className="hre-fl" id="hre-type-label">고용 형태</span>
                  <span className="hre-chips" role="radiogroup" aria-labelledby="hre-type-label">
                    {employmentTypes.map((t) => (
                      <label key={t.key} className="hre-choice">
                        <input type="radio" name="hre-type" checked={hrValue('employmentType') === t.key} disabled={!hr}
                          onChange={() => setHrDraft({ ...hrDraft, employmentType: t.key })} />
                        <span>{t.label}</span>
                      </label>
                    ))}
                  </span>
                  {!hrValue('employmentType') && <small>미설정</small>}</div>
                <div className="hre-f hre-f--wide"><span className="hre-fl" id="hre-status-label">재직 상태</span>
                  <span className="hre-chips" role="radiogroup" aria-labelledby="hre-status-label">
                    {Object.entries(EMPLOYMENT_STATUS_LABEL).map(([k, v]) => (
                      <label key={k} className="hre-choice">
                        <input type="radio" name="hre-status" checked={hrValue('employmentStatus') === k} disabled={!hr}
                          onChange={() => setHrDraft({ ...hrDraft, employmentStatus: k })} />
                        <span>{v}</span>
                      </label>
                    ))}
                  </span></div>
                {detail.hr.worksiteId !== undefined ? (
                  <Field label="근무지" hint="비우면 본사 소속으로 봅니다 · 사업장 추가는 근태의 사업장 화면">
                    {(a) => (hr && worksites ? (
                      <select {...a} className="hre-in" value={hrValue('worksiteId')}
                        onChange={(e) => setHrDraft({ ...hrDraft, worksiteId: e.target.value })}>
                        <option value="">본사 소속 (지정 안 함)</option>
                        {detail.hr.worksiteId && !worksites.some((w) => w.id === detail.hr.worksiteId) && (
                          <option value={detail.hr.worksiteId}>{detail.hr.worksiteName ?? detail.hr.worksiteId}</option>
                        )}
                        {worksites.filter((w) => w.isActive || w.id === detail.hr.worksiteId).map((w) => (
                          <option key={w.id} value={w.id}>{w.name}{w.isActive ? '' : ' (사용 안 함)'}</option>
                        ))}
                      </select>
                    ) : <input {...a} className="hre-in" type="text" value={detail.hr.worksiteName ?? '본사 소속'} readOnly />)}
                  </Field>
                ) : extraField('근무지', extra.workplace, '근무지')}
                <Field label="적용 근무제" hint="근무제 화면에서 배정">
                  {(a) => <input {...a} className="hre-in" type="text" value={`${detail.schedule.name} · ${detail.schedule.baseStart}–${detail.schedule.baseEnd}`} readOnly />}
                </Field>
                <Field label="근속">
                  {(a) => <input {...a} className="hre-in" type="text" value={detail.hr.tenureLabel ?? '미설정'} readOnly />}
                </Field>
                <Field label="올해 연차" hint="입사일 기준 자동 계산">
                  {(a) => <input {...a} className="hre-in num" type="text" value={detail.hr.annualDays === null ? '미설정' : `${detail.hr.annualDays}일`} readOnly />}
                </Field>
                <Field label="회사 이메일" hint="관리 콘솔의 사용자 관리에서 고칩니다">
                  {(a) => <input {...a} className="hre-in" type="text" value={detail.account.email} readOnly />}
                </Field>
                <Field label="역할 · 계정" hint="이메일 · 사번 · 역할 · 비밀번호 · 계정 활성은 관리 콘솔의 사용자 관리">
                  {(a) => <input {...a} className="hre-in" type="text" value={`${detail.account.role} · ${detail.account.isActive ? '사용 중' : '비활성'}`} readOnly />}
                </Field>
                {/* 2026-09-13 · K — 계정 · 권한 · 부서 편집은 종전 직원 관리 화면의 일이다. 그 사람을 연 채로 보낸다. */}
                <div className="hre-f"><span className="hre-fl">계정 · 권한 · 부서</span>
                  <Link className="hre-btn hre-btn--line hre-btn--sm hre-acclink" to={`${STAFF_PATH}?userId=${encodeURIComponent(userId)}`}>계정 · 권한 · 부서 관리 →</Link>
                  <small>권한 바꾸기 · 임시 비밀번호 · 팀 옮기기 · 팀장 지정은 종전 직원 관리 화면에서 합니다</small></div>
              </div></section>
              <p className={`hre-fnote${detail.hr.hireDate ? '' : ' hre-fnote--warn'}`}>
                {detail.hr.hireDate ? '입사일이 있어 올해 연차가 계산됩니다' : '입사일이 없어 연차를 계산할 수 없습니다 — 잔여가 “미설정”입니다'}
              </p>
            </>
          )}
          {tab === 'career' && <RecordPane cols={['회사', '부서 · 직무', '재직 기간', '인정', '증빙']} add="경력 추가" fields={['회사명', '직무', '시작일', '종료일']} />}
          {tab === 'edu' && <RecordPane cols={['학교', '전공', '학위', '재학 기간', '상태']} add="학력 추가" fields={['학교', '전공', '학위', '졸업일']} />}
          {tab === 'cert' && <RecordPane cols={['자격 · 시험', '발급 기관 · 점수', '취득 · 응시일', '유효 기한']} add="자격 · 어학 추가" fields={['명칭', '발급 기관', '취득 · 응시일', '만료일']} />}
          {tab === 'family' && <RecordPane cols={['관계', '성명', '생년월일', '부양가족', '비고']} add="가족 추가" fields={['관계', '가족 성명', '생년월일', '비고']} />}
          {tab === 'moves' && (
            <>
              <ol className="hre-flow">
                {detail.hr.hireDate && (
                  <li className="now"><span className="fm" /><div><b>입사 · {detail.hr.teamName ?? '팀 미배정'}</b><span className="num">{detail.hr.hireDate}</span></div></li>
                )}
              </ol>
              <p className="hre-fnote hre-fnote--muted">승진 · 전보 · 보임 이력 — {NOT_READY_TEXT}</p>
              <AddBox title="새 발령 기안" fields={['발령 구분', '발령일', '발령 소속', '사유']} button="결재 올리기" />
            </>
          )}
        </div>

        <div className="hre-savebar">
          <span>{dirty ? <>고친 칸 <b>{dirtyCount}개</b> · 저장하면 고친 칸만 보냅니다</> : '입력한 내용은 저장을 눌러야 반영됩니다 · 변경 이력은 감사 로그에 남습니다'}</span>
          <button type="button" className="hre-btn hre-btn--line hre-btn--sm" disabled={!dirty || busy}
            onClick={() => { setHrDraft({}); setIdDraft({}); }}>되돌리기</button>
          <button type="button" className="hre-btn hre-btn--sm" disabled={!hr || !dirty || busy}
            onClick={() => { void onSave(); }}>{busy ? '저장 중…' : '저장'}</button>
        </div>
      </section>
    </div>
  );
}

/** 인사기록 탭 — 서버(후속 요청 ⑧)가 붙기 전에는 표 머리 · 빈 줄 · 잠긴 추가 상자만 그린다. */
function RecordPane({ cols, add, fields }: { cols: string[]; add: string; fields: string[] }) {
  return (
    <>
      <div className="hre-tw">
        <table className="hre-table">
          <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody><tr className="none"><td colSpan={cols.length}>{NOT_READY_TEXT}</td></tr></tbody>
        </table>
      </div>
      <AddBox title={add} fields={fields} button="등록" />
    </>
  );
}

function AddBox({ title, fields, button }: { title: string; fields: string[]; button: string }) {
  return (
    <div className="hre-addbox">
      <h4>{title}</h4>
      <div className="hre-fgrid">
        {fields.map((f) => (
          <Field key={f} label={f}>
            {(a) => <input {...a} className="hre-in" type="text" value="" placeholder={NOT_READY_TEXT} disabled />}
          </Field>
        ))}
      </div>
      <div className="hre-addrow">
        <span className="hre-hint">서버가 연결되면 여기서 등록합니다</span>
        <button type="button" className="hre-btn hre-btn--sm" disabled>{button}</button>
      </div>
    </div>
  );
}

export default EmployeeDetailPage;
